/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ServiceKey } from 'src/core/stores/AzureSessionStore';
import { FileType } from 'src/core/types/FileType';
import { LuisRequestOptions } from 'src/core/types/LuisRequestOptions';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { getUserSessionId } from 'src/core/utils/userSessionId';
import { AxiosProgressEvent } from 'axios';

export type SupportedContentTypes = 'json' | 'plainText' | 'bin' | 'xml' | 'merge-patch+json';

/**
 * A builder pattern object to create requests for
 * the http service to use.
 *
 * @param options An optional options object to start building on.
 */
export const luisRequestOptionsBuilder = (options: LuisRequestOptions = {}) => {
    if (!options.headers) {
        options.headers = {};
    }

    let disableUserSessionId = false;

    const actions = {
        useCORS: () => {
            options.withCredentials = true;

            return actions;
        },

        /**
         * Specifies the content type to be returned by
         * the request.
         */
        useContentType: (contentType: SupportedContentTypes) => {
            switch (contentType) {
                case 'json':
                    options.headers['Content-Type'] = 'application/json; charset=utf-8';
                    break;
                case 'plainText':
                    options.headers['Content-Type'] = 'text/plain; charset=utf-8';
                    break;
                case 'xml':
                    options.headers['Content-Type'] = 'application/xml';
                    break;
                case 'bin':
                    options.headers['Content-Type'] = 'application/octet-stream';
                    break;
                case 'merge-patch+json':
                    options.headers['Content-Type'] = 'application/merge-patch+json; charset=utf-8';
                    break;
                default:
            }

            return actions;
        },

        /**
         * Used to attach the given token for Azure
         * related API calls.
         *
         * @param token The token to attach in the header.
         */
        useArmToken: (token: string) => {
            options.headers.Authorization = `Bearer ${token}`;

            return actions;
        },

        /**
         * @todo To be confirmed with backend team if this is a
         * permanent fix
         * The custom arm token header to use containing the user's
         * ARM token used to validate azure accounts information.
         *
         * @param token The token to attach in the header.
         */
        useArmTokenHeader: (token: string) => {
            options.headers.ArmToken = token;

            return actions;
        },

        /**
         * Used to attach the aad token to the authorization header in the request.
         *
         * @param token The cognitive services token used for the APIs authentication.
         */
        useCognitiveServicesTokenHeader: (token: string) => {
            options.headers.Authorization = `Bearer ${token}`;

            return actions;
        },

        /**
         * Used to attach the service key to the headers
         * to authenticate with our platform gateway. The
         * header name differs between the environment in
         * use. For staging environments, the header has a
         * different string.
         *
         * @param serviceKey The key to use with the headers.
         * @param isProduction Whether the current environment
         * is production or staging.
         */
        useAuthoringKey: (serviceKey: string, isProduction: boolean = true, resourceId?: string) => {
            if (isProduction) {
                options.headers['Ocp-Apim-Subscription-Key'] = serviceKey;
            } else {
                options.headers['Apim-Subscription-Id'] = serviceKey;
                options.headers['Azure-Resource-Id'] = resourceId ?? 'SET';
            }

            return actions;
        },

        /**
         * Decide which way should be used to authenticate the APIs
         * based on the service key object type.
         *
         * @param serviceKey Object holds the key type pair to authenticated with.
         * @param isProduction Whether the current environment
         * is production or staging.
         */
        useProperServiceKey: (serviceKey: ServiceKey, isProduction: boolean = true) => {
            if (serviceKey.keyType === 'resourceKey') {
                actions.useAuthoringKey(serviceKey.value, isProduction);
            } else if (serviceKey.keyType === 'aadToken') {
                actions.useCognitiveServicesTokenHeader(serviceKey.value);
            }

            return actions;
        },

        /**
         * Used to attach download trackers on the request
         * to download a file. This allows us to track the
         * download progress of large files.
         *
         * @see trackUpload()
         *
         * @param callback The function to fire when a progress
         * event is received.
         */
        trackDownload: (callback: (event?: AxiosProgressEvent) => void) => {
            options.onDownloadProgress = callback;

            return actions;
        },

        /**
         * Used to attach upload trackers on the request to
         * download a file. This allows us to track the upload
         * progress of a large file.
         *
         * @see trackDownload()
         *
         * @param callback The function to fire when a progress
         * event is received.
         */
        trackUpload: (callback: (event?: AxiosProgressEvent) => void) => {
            options.onUploadProgress = callback;

            return actions;
        },

        /**
         * Uses a combination of options that constitute the requirements
         * of 90% or more of the calls created in the portal.
         *
         * @param serviceKey The key to use to call the APIs.
         * @param isProduction Whether the current environment
         * is production or staging.
         */
        useDefaultOptions: (serviceKey: ServiceKey, isProduction: boolean = true) => {
            actions.useContentType('json').useProperServiceKey(serviceKey, isProduction);

            // Custom header to add backend feature flags if exists
            if (!isProduction) {
                const backendFeatureFlags = LuisConstants.BackendFeatureFlags;
                if (backendFeatureFlags.length) {
                    actions.useCustomHeader('apim-resource-arm-features', backendFeatureFlags.join(';'));
                }
            }
            return actions;
        },

        /**
         * Uses the correct file content based
         * on the given file type.
         *
         * @param fileType The file type to use.
         */
        useFileContent: (fileType: FileType) => {
            switch (fileType) {
                case 'lu':
                case 'csv':
                    actions.useContentType('plainText');
                    break;
                case 'bin':
                    actions.useContentType('bin');
                    break;
                case 'json':
                default:
                    actions.useContentType('json');
                    break;
            }

            return actions;
        },

        /**
         * Set the flag to disable the attachment of the
         * current user session id to the request headers.
         */
        disableUserSessionId: () => {
            disableUserSessionId = true;

            return actions;
        },

        /**
         * Sets a skip-throttling header to disable
         * the rate limiter interceptor.
         */
        skipThrottling: () => {
            options.headers['skip-throttling'] = 'true';

            return actions;
        },

        /**
         * Adds a custom header with the given key
         * and value.
         */
        useCustomHeader: (key: string, value: string) => {
            options.headers[key] = value;

            return actions;
        },
        /**
         * Remove a custom header with the given key
         */
        useRemoveHeader: (key: string) => {
            delete options.headers[key];
            return actions;
        },

        /**
         * Returns the underlying options object that is mutated
         * by the builder object.
         */
        build: (): LuisRequestOptions => {
            /*
             * Attach the current user session id
             * to the request headers.
             * This unique id will be added to the BE logs
             * to correlated set of action user performed
             * in one given active session on the portal with
             * the corresponding BE activities.
             */
            if (!disableUserSessionId) {
                options.headers['User-Session-Id'] = getUserSessionId();
            }

            return options;
        }
    };

    return actions;
};
